import { AuthenticationProvider } from '@zenbusiness/application-commons-authentication';
import { BusinessEntityProvider } from '@zenbusiness/application-commons-business-entity';
import { ClickstreamProvider } from '@zenbusiness/application-commons-clickstream';
import { ExperimentsIdentity, ExperimentsProvider } from '@zenbusiness/application-commons-experiments';
import { GraphQLProvider } from '@zenbusiness/application-commons-graphql';
import { IdentityProvider } from '@zenbusiness/application-commons-identity';
import { IntercomProvider } from '@zenbusiness/application-commons-intercom';
import { LoggingProvider } from '@zenbusiness/application-commons-logging';
import { SnackbarProvider } from 'notistack';

import ErrorBoundary from '#client/components/ErrorBoundary';
import IdentityManager from '#client/components/IdentityManager';
import MinSiteRequirements from '#client/components/MinSiteRequirements/MinSiteRequirements';
import TrackView from '#client/components/TrackView';
import VisitManager from '#client/components/VisitManager';
import ZenThemeWrapper from '#client/components/ZenThemeWrapper';
import { errorHandler } from '#client/errorHandler';
import { VisitProvider } from '#client/hooks/useVisit';
import { CLIENTS, ENV, GRAPHQL_ROUTE } from '#client/manifest';
import ZenRouter from '#client/routes';
import avo from '#client/utils/avo_new';
import isWebView from '#client/utils/isWebView';

/**
 * If your federated MFE uses businessEntityUuid in its urls in a pattern
 * that isn't within /d/company/uuid, then that pattern must be added to
 * this list, or else the BusinessEntityProvider won't know how to read
 * your url.
 */
const bePaths = [
  '/d/company/:businessEntityUuid/*',
  '/d/b/:businessEntityUuid/*',
  '/d/e/:businessEntityUuid/*',
  '/d/i/:businessEntityUuid/*',
  '/d/x/company/:businessEntityUuid/*'
];

function App() {
  return (
    <LoggingProvider
      name="zendash"
      sampleRate={ENV.SENTRY_SAMPLE_RATE}
      tracing={ENV.SENTRY_TRACING}
    >
      <ZenThemeWrapper>
        <MinSiteRequirements>
          <ErrorBoundary>
            <ExperimentsProvider appId="zendash" clientId={ENV.LD_CLIENT_ID}>
              <AuthenticationProvider {...CLIENTS.auth0} isWebView={isWebView}>
                {({ obo }) => (
                  // Enter noop mode if currently OBO, so no events are actually delivered
                  <ClickstreamProvider
                    avo={avo}
                    noop={!!obo}
                    deployEnv={ENV.DEPLOY_ENV}
                    rudderstackApiKey={ENV.RUDDERSTACK_API_KEY}
                    rudderstackApiUrl={ENV.RUDDERSTACK_API_URL}
                  >
                    <IntercomProvider appId={ENV.INTERCOM_APPID}>
                      <GraphQLProvider url={GRAPHQL_ROUTE} onError={errorHandler}>
                        <IdentityProvider>
                          <BusinessEntityProvider path={bePaths}>
                            <ExperimentsIdentity />
                            <VisitProvider>
                              <SnackbarProvider maxSnack={1}>
                                {/* Tracking */}
                                <VisitManager />
                                <TrackView />
                                {/* Identity */}
                                <IdentityManager />
                                {/* Route/Template Error Boundry */}
                                <ErrorBoundary>
                                  <ZenRouter />
                                </ErrorBoundary>
                              </SnackbarProvider>
                            </VisitProvider>
                          </BusinessEntityProvider>
                        </IdentityProvider>
                      </GraphQLProvider>
                    </IntercomProvider>
                  </ClickstreamProvider>
                )}
              </AuthenticationProvider>
            </ExperimentsProvider>
          </ErrorBoundary>
        </MinSiteRequirements>
      </ZenThemeWrapper>
    </LoggingProvider>
  );
}

export default App;
